import { isCancelled } from '@/helpers';
import { _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import { canEditOpenStudySettings, canEditStudySettings, canWriteStudy } from '@/support/StudyAccess';

import type { OverviewSectionKey } from './OverviewAccordion.model';

/**
 * Mirrors StudyNav: Treatment groups is only offered while a study is in
 * progress or awaiting review, and never once it is cancelled.
 */
const treatmentGroupsAvailable = (study: Study): boolean => {
  if (isCancelled(study)) {
    return false;
  }

  return study.status === 'in_progress' || study.status === 'awaiting_approval';
};

export const canUpdateOverviewTask = (study: Study | undefined): boolean => canEditOpenStudySettings(study);

export const canUpdateOverviewAlert = (study: Study | undefined): boolean => canEditOpenStudySettings(study);

/**
 * Whether Manage may link to the page that owns this section. Each destination
 * keeps its own access rule: study-groups write, samples write, or settings
 * (write, including a completed study, or a team owner allowed into settings).
 */
export const canManageOverviewSection = (
  sectionKey: OverviewSectionKey,
  study: Study | undefined,
  diseaseInduction: boolean
): boolean => {
  if (!_notNil(study)) {
    return false;
  }

  switch (sectionKey) {
    case 'groups':
    case 'treatments':
      return treatmentGroupsAvailable(study) && canWriteStudy(study);
    case 'diseases':
      return diseaseInduction === true && treatmentGroupsAvailable(study) && canWriteStudy(study);
    case 'sampling_plan':
      return canWriteStudy(study);
    case 'metadata':
    case 'tasks':
    case 'preset':
    case 'alerts':
      return canEditStudySettings(study);
  }
};
